import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, Typography, Tooltip } from '@mui/material';
import {
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  Videocam as VideoIcon,
  Audiotrack as AudioIcon,
  PictureAsPdf as PdfIcon,
  FolderZip as ArchiveIcon,
  Description as DocumentIcon,
  CloudDone as ReachableIcon,
  CloudOff as UnreachableIcon,
  WarningAmber as UnverifiedIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Telegram as TelegramIcon,
  ContentCopy as CopyIcon,
  Check as CopiedIcon,
  ErrorOutline as CopyFailedIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Message } from '@/types/api';
import { format, parseISO } from 'date-fns';
import { isOutOfScope } from '@/api/media';
import { useMediaAvailability } from '@/hooks/useMedia';
import MediaDeliveryPanel from './MediaDeliveryPanel';
import { formatFileSize } from '@/utils/formatters';
import { mediaDisplayName, mediaKind, mediaLabelKey } from '@/utils/mediaFile';
import { buildTelegramLink } from '@/utils/telegramLink';
import { useChatTheme } from '../ChatThemeContext';

interface TelegramMessageBubbleProps {
  message: Message;
  /**
   * `chats.chat_id` — the Telegram-marked peer id of the chat being previewed,
   * passed down rather than fetched per bubble (see utils/telegramLink.ts).
   * Nullable in the DB; null simply means no link control on these bubbles.
   */
  peerId?: number | null;
  /** Briefly ringed after a search jump, so the target is findable at a glance. */
  highlighted?: boolean;
}

type CopyState = 'idle' | 'copied' | 'failed';

/** How long the copy result stays visible before the icon returns to normal. */
const COPY_FEEDBACK_MS = 1600;

const formatTime = (dateString: string | null): string => {
  if (!dateString) return '';
  try {
    return format(parseISO(dateString), 'h:mm a');
  } catch {
    return '';
  }
};

const formatDate = (dateString: string | null): string => {
  if (!dateString) return '';
  try {
    return format(parseISO(dateString), 'MMM d, yyyy');
  } catch {
    return '';
  }
};

/**
 * PDF (49.4% of the corpus) and archives (44.2%) are almost all of it, so they
 * get their own glyphs instead of sharing the generic file icon. The bucketing
 * lives in `utils/mediaFile.ts`, which knows every mime spelling in the table —
 * RAR alone arrives under four of them.
 */
const getMediaIcon = (mimeType: string | null | undefined) => {
  switch (mediaKind(mimeType)) {
    case 'image': return <ImageIcon fontSize="small" />;
    case 'video': return <VideoIcon fontSize="small" />;
    case 'audio': return <AudioIcon fontSize="small" />;
    case 'pdf': return <PdfIcon fontSize="small" />;
    case 'archive': return <ArchiveIcon fontSize="small" />;
    case 'document': return <DocumentIcon fontSize="small" />;
    default: return <FileIcon fontSize="small" />;
  }
};

const TelegramMessageBubble: React.FC<TelegramMessageBubbleProps> = ({
  message,
  peerId,
  highlighted = false,
}) => {
  const { colors } = useChatTheme();
  const { t } = useTranslation('pages');
  const hasText = !!message.message_text && message.message_text.trim() !== '';
  const hasMedia = !!message.media;
  const time = formatTime(message.timestamp);
  const link = buildTelegramLink(peerId, message.message_id);

  const [copyState, setCopyState] = useState<CopyState>('idle');
  const copyTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(copyTimer.current), []);

  // Availability is asked for, never volunteered: a chat page renders ~50
  // bubbles and this is a per-message route. `message.id` is the internal
  // Messages PK — the route resolves the channel from it, so no Telegram peer
  // id goes into a URL.
  const [showAvailability, setShowAvailability] = useState(false);
  const availability = useMediaAvailability(message.id, hasMedia && showAvailability);
  const displayName = mediaDisplayName(message.media, message.message_id);

  const toggleAvailability = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    setShowAvailability((open) => !open);
  };

  // Only the glyph carries the verdict colour. The sentence stays in the
  // bubble's own text colour, because the bubble is dark blue in one theme and
  // pale green in the other and no single accent is readable on both — the
  // same reason the copy icon colours the icon and not its tooltip.
  const statusLine = (icon: React.ReactNode, text: string, color: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ display: 'flex', color, '& svg': { fontSize: '0.85rem' } }}>{icon}</Box>
      <Typography variant="caption" sx={{ color: colors.bubbleText, fontSize: '0.65rem', lineHeight: 1.5 }}>
        {text}
      </Typography>
    </Box>
  );

  const note = (text: string) => (
    <Typography
      variant="caption"
      sx={{ color: colors.bubbleTimestamp, fontSize: '0.65rem', lineHeight: 1.5 }}
    >
      {text}
    </Typography>
  );

  /**
   * Whether a manuscript scraper account could fetch this file. Phase 1 stopped
   * here; Phase 4 adds `MediaDeliveryPanel` below, which asks the cache and can
   * request, preview and save the bytes. An unreachable file still gets the
   * open-in-Telegram link — and note the two are INDEPENDENT: a file already in
   * the server's cache is served whatever this says, because `/stream` never
   * consults membership.
   */
  const renderAvailability = () => {
    if (availability.isPending) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CircularProgress size={10} sx={{ color: colors.bubbleTimestamp }} />
          {note(t('chatPreview.media.availability.checking'))}
        </Box>
      );
    }

    if (availability.isError) {
      // A 404 is an answer, not a failure: the message is outside the
      // manuscript channel set and the backend refuses to say more than that.
      return statusLine(
        <UnreachableIcon />,
        t(isOutOfScope(availability.error)
          ? 'chatPreview.media.availability.outOfScope'
          : 'chatPreview.media.availability.error'),
        colors.bubbleTimestamp
      );
    }

    const data = availability.data;
    if (!data) return null;

    if (data.downloadable) {
      const unverified = data.reason === 'unverified';
      return (
        <>
          {statusLine(
            unverified ? <UnverifiedIcon /> : <ReachableIcon />,
            t(unverified
              ? 'chatPreview.media.availability.unverified'
              : 'chatPreview.media.availability.available',
              { account: data.account_label ?? '' }),
            unverified ? 'warning.main' : 'success.main'
          )}
          {/* Pure digits inside RTL prose — a localized month name here comes
              out reordered (the join-ETA bug, manuscript/CLAUDE.md). */}
          {note(data.verified_at
            ? t('chatPreview.media.availability.verifiedAt', {
                date: format(parseISO(data.verified_at), 'yyyy-MM-dd'),
              })
            : t('chatPreview.media.availability.neverVerified'))}
        </>
      );
    }

    return (
      <>
        {statusLine(
          <UnreachableIcon />,
          // `no_file` is a property of the media row, not of the channel: the
          // account may be a verified member and there is still nothing to
          // fetch. It must not render as "no account is in this channel",
          // which sends a reviewer looking for a membership fix that would
          // change nothing.
          t(data.reason === 'left'
            ? 'chatPreview.media.availability.left'
            : data.reason === 'no_file'
              ? 'chatPreview.media.availability.noFile'
              : 'chatPreview.media.availability.noAccount'),
          'error.main'
        )}
        {link && (
          <Typography
            component="a"
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            variant="caption"
            sx={{ color: colors.bubbleLink, fontSize: '0.65rem', lineHeight: 1.5 }}
          >
            {t('chatPreview.media.availability.openInTelegram')}
          </Typography>
        )}
      </>
    );
  };

  // A copy that silently does nothing is worse than no button: the result is
  // always shown, success or failure. `navigator.clipboard` is unavailable in
  // insecure contexts, which is a failure the user must see, not a no-op.
  const handleCopy = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!link) return;
    try {
      if (!navigator.clipboard?.writeText) throw new Error('clipboard unavailable');
      await navigator.clipboard.writeText(link);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
    window.clearTimeout(copyTimer.current);
    copyTimer.current = window.setTimeout(() => setCopyState('idle'), COPY_FEEDBACK_MS);
  };

  const copyTitle =
    copyState === 'copied'
      ? t('chatPreview.messageActions.copied')
      : copyState === 'failed'
        ? t('chatPreview.messageActions.copyFailed')
        : t('chatPreview.messageActions.copyLink');

  return (
    <Tooltip title={`#${message.message_id} — ${formatDate(message.timestamp)}`} placement="left">
      <Box
        sx={{
          maxWidth: '80%',
          mb: 0.5,
          ml: 'auto',
          mr: 1,
        }}
      >
        <Box
          sx={{
            bgcolor: colors.bubbleChannel,
            color: colors.bubbleText,
            borderRadius: '12px 12px 4px 12px',
            px: 1.5,
            py: 0.75,
            position: 'relative',
            outline: highlighted ? `2px solid ${colors.bubbleLink}` : 'none',
            boxShadow: highlighted ? `0 0 0 4px ${colors.bubbleLink}33` : 'none',
            transition: 'outline-color 200ms, box-shadow 200ms',
            // The link controls sit on every message, so they stay faint until
            // the bubble is hovered (and always visible to keyboard focus).
            '&:hover .msg-link-actions, & .msg-link-actions:focus-within': { opacity: 1 },
          }}
        >
          {/* Media indicator — the file card. Clicking it asks whether any
              manuscript account can reach the file. */}
          {hasMedia && (
            <Box
              sx={{
                mb: hasText ? 0.5 : 0,
                p: 0.75,
                bgcolor: 'rgba(0,0,0,0.1)',
                borderRadius: 1,
              }}
            >
              <Box
                role="button"
                tabIndex={0}
                aria-expanded={showAvailability}
                aria-label={t('chatPreview.media.availability.toggle')}
                onClick={toggleAvailability}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    toggleAvailability(event);
                  }
                }}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer' }}
              >
                {getMediaIcon(message.media?.mime_type)}
                <Box sx={{ overflow: 'hidden', flex: 1 }}>
                  {/* <bdi>, not dir="ltr": names are Arabic as often as Latin and
                      forcing either direction scrambles the other. First-strong
                      isolation gets both right and keeps the extension attached. */}
                  <Typography
                    component="bdi"
                    variant="caption"
                    noWrap
                    sx={{ color: colors.bubbleText, display: 'block' }}
                  >
                    {displayName || t(mediaLabelKey(message.media?.mime_type))}
                  </Typography>
                  {/* `!= null`, not truthiness: 14,055 rows carry NULL file_size
                      (the photo insert path writes none) and a 0-byte file is
                      still a file — neither is the same as "no size". */}
                  {message.media?.file_size != null && (
                    <Typography variant="caption" sx={{ color: colors.bubbleTimestamp, fontSize: '0.65rem' }}>
                      {formatFileSize(message.media.file_size)}
                    </Typography>
                  )}
                </Box>
                {showAvailability
                  ? <ExpandLessIcon sx={{ fontSize: '0.9rem', color: colors.bubbleTimestamp }} />
                  : <ExpandMoreIcon sx={{ fontSize: '0.9rem', color: colors.bubbleTimestamp }} />}
              </Box>

              {showAvailability && (
                <Box
                  sx={{
                    mt: 0.75,
                    pt: 0.75,
                    borderTop: `1px solid ${colors.bubbleTimestamp}44`,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.25,
                  }}
                >
                  {renderAvailability()}
                  {/* Delivery is rendered only once availability answered, and
                      never for `no_file`: with no `file_id` no cache key can
                      exist, so every delivery route would answer the same
                      "nothing here" three different ways. */}
                  {availability.data && availability.data.reason !== 'no_file' && (
                    <MediaDeliveryPanel
                      message={message}
                      canRequest={availability.data.downloadable}
                    />
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Message text */}
          {hasText && (
            <Typography
              variant="body2"
              sx={{
                direction: 'rtl',
                textAlign: 'right',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                fontSize: '0.875rem',
                lineHeight: 1.5,
              }}
            >
              {message.message_text}
            </Typography>
          )}

          {/* Empty message */}
          {!hasText && !hasMedia && (
            <Typography variant="caption" sx={{ color: colors.bubbleTimestamp, fontStyle: 'italic' }}>
              {t('chatPreview.messagePanel.emptyMessage')}
            </Typography>
          )}

          {/* Footer: Telegram link controls + timestamp */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 0.25,
              mt: 0.25,
            }}
          >
            {link && (
              <Box
                className="msg-link-actions"
                sx={{ display: 'flex', opacity: 0.45, transition: 'opacity 120ms' }}
              >
                <Tooltip title={t('chatPreview.messageActions.openInTelegram')}>
                  <IconButton
                    size="small"
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ p: 0.25, color: colors.bubbleTimestamp }}
                  >
                    <TelegramIcon sx={{ fontSize: '0.9rem' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={copyTitle}>
                  <IconButton
                    size="small"
                    onClick={handleCopy}
                    aria-label={t('chatPreview.messageActions.copyLink')}
                    sx={{
                      p: 0.25,
                      color:
                        copyState === 'failed'
                          ? 'error.main'
                          : copyState === 'copied'
                            ? 'success.main'
                            : colors.bubbleTimestamp,
                    }}
                  >
                    {copyState === 'copied' ? (
                      <CopiedIcon sx={{ fontSize: '0.9rem' }} />
                    ) : copyState === 'failed' ? (
                      <CopyFailedIcon sx={{ fontSize: '0.9rem' }} />
                    ) : (
                      <CopyIcon sx={{ fontSize: '0.9rem' }} />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            {time && (
              <Typography
                variant="caption"
                sx={{ color: colors.bubbleTimestamp, fontSize: '0.65rem' }}
              >
                {time}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Tooltip>
  );
};

export default TelegramMessageBubble;
